import { useContext, useEffect, useMemo, useSyncExternalStore } from 'react'
import { useLocation, useParams, UNSAFE_DataRouterStateContext } from 'react-router-dom'
import { useActiveBranchRecord } from '@axilon-apps/domains-ontology-ui-state'

import { clampAppContextEntries } from './clamp'
import { produceRouteBaselineEntries } from './routeBaseline'
import { resolveRoutePattern, type RouteMatchLike } from './resolveRoutePattern'
import type { AppContextEntry } from './types'

interface PageOverrides {
  entries: AppContextEntry[]
  /**
   * Baseline keys the page's entries stand in for — the raw URL params it
   * has enriched into `name + status + id`. Dropped from the baseline so
   * the same id is not offered twice.
   */
  supersedes: string[]
}

const NO_OVERRIDES: PageOverrides = { entries: [], supersedes: [] }

/**
 * Module-level page-override store. Only one page is mounted at a time, so
 * a single slot is enough — pages call {@link useChatAppContext} with entries
 * to register, and the chat controller reads the merged snapshot via
 * {@link useMergedChatAppContext} (or the no-arg form of this hook).
 */
let pageOverrides: PageOverrides = NO_OVERRIDES
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getPageOverrideSnapshot(): PageOverrides {
  return pageOverrides
}

function setPageOverrides(next: PageOverrides): void {
  pageOverrides = next
  for (const listener of listeners) listener()
}

/**
 * The global frame, which leads every snapshot: without it a page entry
 * like `Node label: Compute` reads as a claim about which page the user is
 * on. A page may override either value, but not their position.
 */
const FRAME_KEYS: readonly string[] = ['Page', 'Branch']

/**
 * Merge page-registered overrides onto the route-derived baseline.
 *
 * Order is the admission priority, and follows the per-page convention:
 * global frame, then the page's own entries (deictic anchors first), then
 * whatever raw URL params are left.
 *
 * - Keys that appear in overrides replace the baseline entry with the same
 *   key (case-sensitive), so a page can sharpen `Page` / enrich a bare id.
 * - `supersedes` drops further baseline keys the overrides stand in for —
 *   the raw search/path params behind an enriched entry.
 */
export function mergeAppContextEntries(
  baseline: readonly AppContextEntry[],
  overrides: readonly AppContextEntry[],
  supersedes: readonly string[] = []
): AppContextEntry[] {
  if (overrides.length === 0 && supersedes.length === 0) return [...baseline]

  const overrideByKey = new Map(overrides.map((e) => [e.key, e]))
  const dropped = new Set([...overrideByKey.keys(), ...supersedes])

  const frame: AppContextEntry[] = []
  for (const key of FRAME_KEYS) {
    const entry = overrideByKey.get(key) ?? baseline.find((e) => e.key === key)
    if (entry !== undefined) frame.push(entry)
  }

  return [
    ...frame,
    ...overrides.filter((e) => !FRAME_KEYS.includes(e.key)),
    ...baseline.filter((e) => !dropped.has(e.key) && !FRAME_KEYS.includes(e.key))
  ]
}

/**
 * Register page-specific context entries for the currently mounted page.
 * Pass `entries` to publish; omit / pass `undefined` to only read.
 *
 * `supersedes` names the baseline keys these entries replace — typically the
 * raw URL params the page has enriched (`'dashboard'`, `'selected'`, …).
 *
 * The chat controller should call {@link useMergedChatAppContext} (or this
 * hook with no args) at submit time to attach the snapshot to the request.
 */
export function useChatAppContext(
  entries?: readonly AppContextEntry[],
  supersedes?: readonly string[]
): AppContextEntry[] {
  // Key the registration on content, not array identity: publishing wakes
  // every subscriber (including the caller), so an identity-keyed effect
  // would re-register forever on a page that builds its entries inline.
  const registrationKey = JSON.stringify(entries === undefined ? null : [entries, supersedes ?? []])
  useEffect(() => {
    const registration = JSON.parse(registrationKey) as [AppContextEntry[], string[]] | null
    if (registration === null) return
    setPageOverrides({
      entries: clampAppContextEntries(registration[0]),
      supersedes: registration[1]
    })
    return () => {
      setPageOverrides(NO_OVERRIDES)
    }
  }, [registrationKey])

  return useMergedChatAppContext()
}

/**
 * Matched routes, or none when there is no data router.
 *
 * `useMatches()` throws outside a data router, which would make every page
 * that registers context unrenderable under a plain `<MemoryRouter>` — and
 * a context producer must never break a page. Reading the router state
 * directly degrades to the pathname-based pattern resolution instead, and
 * carries the `route` objects the public `useMatches()` shape strips.
 */
function useRouteMatches(): readonly RouteMatchLike[] {
  const routerState = useContext(UNSAFE_DataRouterStateContext)
  return routerState?.matches ?? []
}

/**
 * Read the merged route-baseline + page-override snapshot for the current
 * location. Stable across re-renders of the same URL + branch + registration.
 */
export function useMergedChatAppContext(): AppContextEntry[] {
  const location = useLocation()
  const matches = useRouteMatches()
  const params = useParams()
  const branch = useActiveBranchRecord()
  const overrides = useSyncExternalStore(
    subscribe,
    getPageOverrideSnapshot,
    getPageOverrideSnapshot
  )

  const routePattern = resolveRoutePattern(matches, location.pathname)
  // Stabilize the returned array so submit handlers depending on it don't
  // churn every render when the URL / branch / override slot are unchanged.
  // Serialize params / overrides / branch for the dep list — objects are new
  // identities every render even when their contents are equal.
  const paramsKey = JSON.stringify(params)
  const overridesKey = JSON.stringify(overrides)
  const branchKey =
    branch === undefined ? '' : JSON.stringify({ name: branch.name, protected: branch.protected })
  return useMemo(() => {
    const parsedParams = JSON.parse(paramsKey) as Record<string, string | undefined>
    const parsedOverrides = JSON.parse(overridesKey) as PageOverrides
    const parsedBranch =
      branchKey === '' ? undefined : (JSON.parse(branchKey) as { name: string; protected: boolean })
    const baseline = produceRouteBaselineEntries({
      routePattern,
      pathname: location.pathname,
      search: location.search,
      ...(parsedBranch !== undefined ? { branch: parsedBranch } : {}),
      params: parsedParams
    })
    return mergeAppContextEntries(baseline, parsedOverrides.entries, parsedOverrides.supersedes)
  }, [routePattern, location.pathname, location.search, branchKey, paramsKey, overridesKey])
}

/** Test-only: reset the page-override slot between specs. */
export function __resetChatAppContextForTests(): void {
  pageOverrides = NO_OVERRIDES
}
